import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Cloud as BucketIcon,
  Description as TextIcon,
  Image as ImageIcon,
  Code as JsonIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';

interface R2Object {
  key: string;
  size: number;
  uploaded: string;
  etag?: string;
}

interface R2Bucket {
  name: string;
  binding: string;
  description: string;
  actualName?: string;
  environment?: string;
}

interface R2ListResponse {
  objects: R2Object[];
  truncated: boolean;
  cursor: string | null;
}

interface R2ObjectContent {
  key: string;
  size: number;
  contentType: string;
  content?: string;
  encoding?: string;
  message?: string;
}

interface FileNode {
  type: 'file';
  name: string;
  fullPath: string;
  obj: R2Object;
}

interface FolderNode {
  type: 'folder';
  name: string;
  fullPath: string;
  children: TreeNode[];
  expanded: boolean;
}

type TreeNode = FileNode | FolderNode;

function buildFileTree(objects: R2Object[]): FolderNode {
  const root: FolderNode = {
    type: 'folder',
    name: '',
    fullPath: '',
    children: [],
    expanded: true,
  };

  for (const obj of objects) {
    const parts = obj.key.split('/');
    let currentFolder = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLastPart = i === parts.length - 1;

      if (isLastPart) {
        // It's a file
        currentFolder.children.push({
          type: 'file',
          name: part,
          fullPath: obj.key,
          obj,
        });
      } else {
        // It's a folder
        const folderPath = parts.slice(0, i + 1).join('/');
        let folder = currentFolder.children.find(
          (child) => child.type === 'folder' && child.name === part
        ) as FolderNode | undefined;

        if (!folder) {
          folder = {
            type: 'folder',
            name: part,
            fullPath: folderPath,
            children: [],
            expanded: false,
          };
          currentFolder.children.push(folder);
        }

        currentFolder = folder;
      }
    }
  }

  // Sort children: folders first, then files
  const sortChildren = (node: FolderNode) => {
    node.children.sort((a, b) => {
      if (a.type === b.type) {
        return a.name.localeCompare(b.name);
      }
      return a.type === 'folder' ? -1 : 1;
    });
    node.children.forEach((child) => {
      if (child.type === 'folder') {
        sortChildren(child);
      }
    });
  };

  sortChildren(root);
  return root;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function getFileIcon(key: string) {
  const ext = key.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'json':
      return <JsonIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
    case 'md':
    case 'txt':
      return <TextIcon sx={{ fontSize: 18, color: 'info.main' }} />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
      return <ImageIcon sx={{ fontSize: 18, color: 'success.main' }} />;
    default:
      return <FileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />;
  }
}

export function R2Browser() {
  const [buckets, setBuckets] = useState<R2Bucket[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [objects, setObjects] = useState<R2Object[]>([]);
  const [fileTree, setFileTree] = useState<FolderNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<R2Object | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileCopied, setFileCopied] = useState(false);

  // Copy file content to clipboard
  const handleCopyFileContent = async () => {
    if (!fileContent) return;
    try {
      await navigator.clipboard.writeText(fileContent);
      setFileCopied(true);
      setTimeout(() => setFileCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy file content:', error);
    }
  };

  // Fetch available buckets on mount
  useEffect(() => {
    const fetchBuckets = async () => {
      const logId = logService.api('GET', '/api/r2/buckets', {}, 'pending');
      try {
        const response = await fetch(`${DATABASE_SERVICE_URL()}/api/r2/buckets`);
        const json = await response.json();
        if (json.success) {
          setBuckets(json.data);
          logService.updateLog(logId, { status: 'success', details: { count: json.data.length } });
        } else {
          logService.updateLog(logId, { status: 'error', message: json.error });
        }
      } catch (err) {
        logService.updateLog(logId, { status: 'error', message: 'Failed to fetch buckets' });
      } finally {
        setBucketsLoading(false);
      }
    };
    fetchBuckets();
  }, []);

  const fetchBucketContents = async (bucketName: string) => {
    setLoading(true);
    setError(null);

    const logId = logService.api('GET', `/api/r2/${bucketName}/objects`, {}, 'pending');

    try {
      const response = await fetch(`${DATABASE_SERVICE_URL()}/api/r2/${bucketName}/objects`);
      const json = await response.json();

      if (json.success) {
        const data = json.data as R2ListResponse;
        setObjects(data.objects);
        setFileTree(buildFileTree(data.objects));
        logService.updateLog(logId, { status: 'success', details: { count: data.objects.length } });
      } else {
        setError(json.error || 'Failed to list objects');
        logService.updateLog(logId, { status: 'error', message: json.error });
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      logService.updateLog(logId, { status: 'error', message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const fetchFileContent = async (bucketName: string, key: string) => {
    setFileLoading(true);

    const logId = logService.api('GET', `/api/r2/${bucketName}/object/${key}`, {}, 'pending');

    try {
      const response = await fetch(`${DATABASE_SERVICE_URL()}/api/r2/${bucketName}/object/${key}`);
      const json = await response.json();

      if (json.success) {
        const data = json.data as R2ObjectContent;
        if (data.content) {
          setFileContent(data.content);
        } else if (data.message) {
          setFileContent(data.message);
        } else {
          setFileContent('No content available');
        }
        logService.updateLog(logId, { status: 'success', details: { contentType: data.contentType } });
      } else {
        setFileContent(`Error: ${json.error}`);
        logService.updateLog(logId, { status: 'error', message: json.error });
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setFileContent(`Error: ${errorMsg}`);
      logService.updateLog(logId, { status: 'error', message: errorMsg });
    } finally {
      setFileLoading(false);
    }
  };

  const handleBucketSelect = (bucketName: string) => {
    setSelectedBucket(bucketName);
    fetchBucketContents(bucketName);
  };

  const handleBack = () => {
    if (selectedFile) {
      setSelectedFile(null);
      setFileContent(null);
    } else {
      setSelectedBucket(null);
      setObjects([]);
      setFileTree(null);
      setError(null);
    }
  };

  const handleRefresh = () => {
    if (selectedBucket) {
      fetchBucketContents(selectedBucket);
    }
  };

  const handleFileSelect = async (obj: R2Object) => {
    setSelectedFile(obj);
    if (selectedBucket) {
      await fetchFileContent(selectedBucket, obj.key);
    }
  };

  const toggleFolder = (folderPath: string) => {
    if (!fileTree) return;

    const toggleInTree = (node: FolderNode): FolderNode => {
      if (node.fullPath === folderPath) {
        return { ...node, expanded: !node.expanded };
      }

      return {
        ...node,
        children: node.children.map((child) =>
          child.type === 'folder' ? toggleInTree(child) : child
        ),
      };
    };

    setFileTree(toggleInTree(fileTree));
  };

  const renderTreeNode = (node: TreeNode, depth: number = 0): JSX.Element | null => {
    if (node.type === 'file') {
      return (
        <ListItem key={node.fullPath} disablePadding sx={{ pl: depth * 2 }}>
          <ListItemButton onClick={() => handleFileSelect(node.obj)}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {getFileIcon(node.name)}
            </ListItemIcon>
            <ListItemText
              primary={node.name}
              secondary={formatBytes(node.obj.size)}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { fontFamily: 'monospace' },
              }}
              secondaryTypographyProps={{
                variant: 'caption',
              }}
            />
          </ListItemButton>
        </ListItem>
      );
    }

    // Folder
    const fileCount = node.children.filter((c) => c.type === 'file').length;
    const folderCount = node.children.filter((c) => c.type === 'folder').length;

    return (
      <Box key={node.fullPath}>
        <ListItem disablePadding sx={{ pl: depth * 2 }}>
          <ListItemButton onClick={() => toggleFolder(node.fullPath)}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {node.expanded ? (
                <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
            </ListItemIcon>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {node.expanded ? (
                <FolderOpenIcon sx={{ fontSize: 18, color: 'warning.main' }} />
              ) : (
                <FolderIcon sx={{ fontSize: 18, color: 'warning.main' }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={node.name}
              secondary={`${folderCount} folders, ${fileCount} files`}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { fontFamily: 'monospace', fontWeight: 600 },
              }}
              secondaryTypographyProps={{
                variant: 'caption',
              }}
            />
          </ListItemButton>
        </ListItem>
        <Collapse in={node.expanded} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {node.children.map((child) => renderTreeNode(child, depth + 1))}
          </List>
        </Collapse>
      </Box>
    );
  };

  // Render bucket list
  if (!selectedBucket) {
    return (
      <Card
        className="panel"
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <CardContent className="panel-header" sx={{ p: 1.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.main',
                fontWeight: 700,
                letterSpacing: 1,
                fontSize: '0.65rem',
              }}
            >
              R2 Storage Browser
            </Typography>
            {buckets.length > 0 && buckets[0].environment && (
              <Chip
                label={buckets[0].environment}
                size="small"
                color={buckets[0].environment === 'staging' ? 'warning' : buckets[0].environment === 'production' ? 'error' : 'default'}
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Select a bucket to browse
          </Typography>
        </CardContent>

        <Box className="panel-scroll">
          {bucketsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <List dense disablePadding>
              {buckets.map((bucket) => (
                <ListItem key={bucket.name} disablePadding>
                  <ListItemButton onClick={() => handleBucketSelect(bucket.name)}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <BucketIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={bucket.actualName || bucket.name}
                      secondary={bucket.description}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontFamily: 'monospace' },
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Card>
    );
  }

  // Render file content view
  if (selectedFile) {
    return (
      <Card
        className="panel"
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <CardContent className="panel-header" sx={{ p: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={handleBack}>
              <BackIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                {selectedFile.key}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatBytes(selectedFile.size)} | {new Date(selectedFile.uploaded).toLocaleString()}
              </Typography>
            </Box>
            <Tooltip title={fileCopied ? 'Copied!' : 'Copy file content'}>
              <IconButton
                size="small"
                onClick={handleCopyFileContent}
                disabled={!fileContent || fileLoading}
                sx={{
                  color: fileCopied ? 'success.main' : 'primary.main',
                  backgroundColor: 'action.hover',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {fileCopied ? <CheckIcon sx={{ fontSize: 18 }} /> : <ContentCopyIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>

        <Divider />

        <Box className="panel-scroll" sx={{ p: 1 }}>
          {fileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                backgroundColor: 'action.hover',
              }}
            >
              {fileContent || 'No content'}
            </Paper>
          )}
        </Box>
      </Card>
    );
  }

  // Render bucket contents
  return (
    <Card
      className="panel"
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent className="panel-header" sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={handleBack}>
            <BackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="subtitle2"
            sx={{ fontFamily: 'monospace', flex: 1 }}
          >
            {selectedBucket}
          </Typography>
          <Chip
            label={`${objects.length} objects`}
            size="small"
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>

      <Divider />

      <Box className="panel-scroll">
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 1.5 }}>
            <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && fileTree && fileTree.children.length > 0 && (
          <List dense disablePadding>
            {fileTree.children.map((child) => renderTreeNode(child, 0))}
          </List>
        )}

        {!loading && !error && objects.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ py: 4 }}
          >
            Bucket is empty
          </Typography>
        )}
      </Box>
    </Card>
  );
}
